import { useLayoutEffect, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getPageSeo } from '../seo/meta';

const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

function setMeta(selector: string, create: () => HTMLElement, content: string) {
  let element = document.querySelector(selector);
  if (!content) {
    element?.remove();
    return;
  }
  if (!element) {
    element = create();
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

/**
 * Applies the route's title, description, canonical, hreflang, social tags and
 * JSON-LD. The prerender step writes the same values into each route's HTML.
 */
const DocumentHead: React.FC = () => {
  const { pathname } = useLocation();
  const seo = getPageSeo(pathname);

  useIsoLayoutEffect(() => {
    document.title = seo.title;
    document.documentElement.lang = seo.lang;

    setMeta('meta[name="description"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      return meta;
    }, seo.description);

    setMeta('meta[name="robots"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      return meta;
    }, seo.robots);

    const languageMeta = document.querySelector('meta[name="language"]');
    languageMeta?.setAttribute('content', seo.lang === 'de' ? 'German' : 'English');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!seo.canonical) {
      canonical?.remove();
    } else {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', seo.canonical);
    }

    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((node) => node.remove());
    for (const alternate of seo.hreflang) {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', alternate.hreflang);
      link.setAttribute('href', alternate.href);
      document.head.appendChild(link);
    }

    setMeta('meta[property="og:title"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      return meta;
    }, seo.ogTitle);
    setMeta('meta[property="og:description"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:description');
      return meta;
    }, seo.ogDescription);
    setMeta('meta[property="og:url"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:url');
      return meta;
    }, seo.canonical);
    setMeta('meta[property="og:locale"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:locale');
      return meta;
    }, seo.ogLocale);
    setMeta('meta[property="og:locale:alternate"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:locale:alternate');
      return meta;
    }, seo.ogLocaleAlternate);
    setMeta('meta[name="twitter:title"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'twitter:title');
      return meta;
    }, seo.ogTitle);
    setMeta('meta[name="twitter:description"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'twitter:description');
      return meta;
    }, seo.ogDescription);

    let jsonLd = document.getElementById('seo-jsonld');
    if (!seo.jsonLd) {
      jsonLd?.remove();
    } else {
      if (!jsonLd) {
        jsonLd = document.createElement('script');
        jsonLd.id = 'seo-jsonld';
        jsonLd.setAttribute('type', 'application/ld+json');
        document.head.appendChild(jsonLd);
      }
      jsonLd.textContent = JSON.stringify(seo.jsonLd);
    }
  }, [seo]);

  return null;
};

export default DocumentHead;
